import { CPopover } from "@coreui/react";
import {
  AUTO_INVOICE_STATUS_APPROVED_BY_SET,
  AUTO_INVOICE_STATUS_CANCELLATION_FAILED,
  AUTO_INVOICE_STATUS_CANCELLATION_REQUESTED,
  AUTO_INVOICE_STATUS_CANCELLED,
  AUTO_INVOICE_STATUS_CHOICES,
  AUTO_INVOICE_STATUS_ERROR,
  AUTO_INVOICE_STATUS_RECOVERABLE_ERROR,
  AUTO_INVOICE_STATUS_RESEND_REQUESTED,
  AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR,
  AUTO_INVOICE_STATUS_UPLOADED_TO_SET,
  AutoInvoice,
} from "../../models/auto-invoice";
import { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import DocumentError from "../../models/document-error";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";

interface AutoInvoiceCrudStatusProps {
  autoInvoice: AutoInvoice;
}

const AutoInvoiceCrudStatus: React.FC<AutoInvoiceCrudStatusProps> = ({
  autoInvoice,
}) => {
  const [currentAutoInvoice, setCurrentAutoInvoice] = useState(autoInvoice);
  const [retrievedError, setRetrievedError] = useState<DocumentError>();
  const [loadingError, setLoadingError] = useState(false);

  useSub(`auto_invoice_${autoInvoice.id}`, (autoInvoice: AutoInvoice) => {
    setCurrentAutoInvoice(autoInvoice);
  });

  useEffect(() => {
    setCurrentAutoInvoice(autoInvoice);
  }, [autoInvoice]);

  useEffect(() => {
    const fetchError = async () => {
      const errorCode = currentAutoInvoice.setResponseCode;
      const errorMessage =
        currentAutoInvoice.setResponseDescription ||
        currentAutoInvoice.setError;

      if (
        !(
          currentAutoInvoice.status === AUTO_INVOICE_STATUS_ERROR ||
          currentAutoInvoice.status ===
            AUTO_INVOICE_STATUS_CANCELLATION_FAILED ||
          currentAutoInvoice.status === AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR
        )
      ) {
        setRetrievedError(undefined);
        return;
      }

      if (!errorCode && !errorMessage) {
        setRetrievedError(undefined);
        return;
      }

      setLoadingError(true);
      try {
        let result;
        if (errorCode) {
          const params = new Map<string, string | undefined>();
          params.set("error_code", errorCode);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (
          (!result || result.status !== SUCCESS || !result.data) &&
          errorMessage
        ) {
          const params = new Map<string, string | undefined>();
          params.set("raw_message", errorMessage);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (result?.status === SUCCESS && result.data) {
          setRetrievedError(result.data);
        } else {
          setRetrievedError(undefined);
        }
      } catch (err) {
        console.error("Error loading document error:", err);
        setRetrievedError(undefined);
      } finally {
        setLoadingError(false);
      }
    };

    fetchError();
  }, [currentAutoInvoice]);

  let badgeClassName = "badge-info";

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_UPLOADED_TO_SET) {
    badgeClassName = "badge-warning";
  }

  if (
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_ERROR ||
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_RECOVERABLE_ERROR
  ) {
    badgeClassName = "badge-danger";
  }

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_APPROVED_BY_SET) {
    badgeClassName = "badge-success";
  }

  if (
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_CANCELLATION_REQUESTED
  ) {
    badgeClassName = "badge-light";
  }

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_CANCELLED) {
    badgeClassName = "badge-info";
  }

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_CANCELLATION_FAILED) {
    badgeClassName = "badge-danger";
  }

  if (currentAutoInvoice.status === AUTO_INVOICE_STATUS_RESEND_REQUESTED) {
    badgeClassName = "badge-warning";
  }

  const renderPopoverContent = () => {
    if (
      ![
        AUTO_INVOICE_STATUS_ERROR,
        AUTO_INVOICE_STATUS_CANCELLATION_FAILED,
        AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR,
      ].includes(currentAutoInvoice.status!)
    )
      return null;

    return (
      <ul
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "0px",
          marginBottom: "0px",
        }}
      >
        {currentAutoInvoice.setResponseCode && (
          <li>Código: {currentAutoInvoice.setResponseCode}</li>
        )}
        {currentAutoInvoice.setResponseDescription && (
          <li>Descripción: {currentAutoInvoice.setResponseDescription}</li>
        )}
        {currentAutoInvoice.setError && (
          <li>Error: {currentAutoInvoice.setError}</li>
        )}
        {!loadingError && retrievedError && (
          <>
            {retrievedError.description && (
              <li>Significado: {retrievedError.description}</li>
            )}
            {retrievedError.possibleSolutions && (
              <li>Posible solución: {retrievedError.possibleSolutions}</li>
            )}
          </>
        )}
      </ul>
    );
  };

  return (
    <td className="text-center">
      <span
        className={`badge badge-pill ${badgeClassName}`}
        style={{ fontSize: "14px" }}
      >
        {AUTO_INVOICE_STATUS_CHOICES.get(currentAutoInvoice.status!)}{" "}
        {renderPopoverContent() ? (
          <CPopover
            content={renderPopoverContent()}
            placement="top"
            trigger="click"
          >
            <span
              className="fa fa-info-circle"
              style={{ cursor: "pointer" }}
            ></span>
          </CPopover>
        ) : null}
      </span>
    </td>
  );
};

export default AutoInvoiceCrudStatus;
